import { FunctionComponent, useEffect, useState } from 'react';
import { useApiClientWrapper } from 'hooks';
import { useSelector } from 'react-redux';
import styles from './EvaluationReportDrawer.module.scss';

// Models & Types
import { AppointmentPet } from '@spike/appointment-model';
import { dispatch, RootState } from 'store';
import { EvaluationReport } from '@spike/report-model';
import Pet from '@spike/pet-model';

// Redux State & Actions
import { getEvaluationReportThunk } from '@spike/reports-action';
import { getPetThunk, PetsStatus } from '@spike/pets-action';

// UI Components
import { Spinner } from 'components/UI';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { AddEditEvaluationReport } from 'components/OrderDetail/AddEditEvaluationReport/AddEditEvaluationReport';
import { ViewEvaluationReport } from './ViewEvaluationReport/ViewEvaluationReport';

export interface EvaluationReportDrawerProps {
    evaluationReportId?: number;
    evaluationReport?: EvaluationReport;
    appointmentId?: number;
    serviceId?: number;
    petId?: number;
    pet?: AppointmentPet;
    defaultViewMode: string;
    lastResult?: boolean;
    onClose?: () => void;
    onSave?: (evaluationReport: EvaluationReport) => void;
    className?: string;
    zIndex?: number;
}

export const EvaluationReportDrawer: FunctionComponent<EvaluationReportDrawerProps> = props => {
    const apiClientWrapper = useApiClientWrapper();
    const [evaluationReport, setEvaluationReport] = useState<EvaluationReport | null>(props.evaluationReport ?? null);
    const pet = useSelector<RootState, Pet | undefined>(state => state.pets.pet);
    const petsStatus = useSelector<RootState, PetsStatus>(state => state.pets.status);
    const [loadingFetch, setLoadingFetch] = useState(true);
    const fetchedPet = props.petId == null || pet?.id === props.petId ? pet : undefined;
    const petToUse: AppointmentPet | undefined = props.pet ?? (fetchedPet as unknown as AppointmentPet);
    const [mode, setMode] = useState<'view' | 'edit'>(() => {
        if (props.defaultViewMode === 'view' && (props.evaluationReportId || props.evaluationReport)) return 'view';
        return 'edit';
    });

    useEffect(() => {
        if (props.evaluationReport || !props.evaluationReportId) return;

        setLoadingFetch(true);
        dispatch(getEvaluationReportThunk(apiClientWrapper, props.evaluationReportId))
            .then((report: EvaluationReport) => {
                setEvaluationReport(report);

                if (!props.pet && report.petId) {
                    dispatch(getPetThunk(apiClientWrapper, report.petId));
                }
            })
            .catch(() => setEvaluationReport(null))
            .finally(() => setLoadingFetch(false));
    }, [props.evaluationReportId, props.evaluationReport]);

    useEffect(() => {
        if (props.pet || !props.petId || props.evaluationReportId || props.evaluationReport) return;

        setLoadingFetch(true);
        dispatch(getPetThunk(apiClientWrapper, props.petId));
    }, [props.pet, props.petId, props.evaluationReportId, props.evaluationReport]);

    useEffect(() => {
        if (props.evaluationReport && !props.pet && !petToUse && props.evaluationReport.petId) {
            dispatch(getPetThunk(apiClientWrapper, props.evaluationReport.petId));
        }
    }, [props.evaluationReport, props.pet, petToUse]);

    useEffect(() => {
        if (petsStatus === PetsStatus.GetSuccess && petToUse) {
            setLoadingFetch(false);
        }
    }, [petsStatus, petToUse]);

    useEffect(() => {
        if (props.evaluationReport && petToUse) {
            setLoadingFetch(false);
        }
    }, [props.evaluationReport, petToUse]);

    return (
        <Drawer
            onClose={props.onClose}
            zIndex={props.zIndex}
        >
            {loadingFetch ? (
                <div className={styles.loadingContainer}>
                    <Spinner className={styles.loading} />
                </div>
            ) : mode === 'view' && evaluationReport && petToUse && props.onClose ? (
                <ViewEvaluationReport
                    evaluationReport={evaluationReport}
                    pet={petToUse}
                    lastResult={props.lastResult}
                    onBack={props.onClose}
                    onEdit={() => {
                        setMode('edit');
                    }}
                />
            ) : (
                ((props.appointmentId && props.serviceId && (props.petId || petToUse)) ||
                    (petToUse && evaluationReport && evaluationReport.appointmentId && evaluationReport.serviceId)) && (
                    <AddEditEvaluationReport
                        evaluationReportId={props.evaluationReportId}
                        appointmentId={(props.appointmentId || evaluationReport?.appointmentId)!}
                        serviceId={(props.serviceId || evaluationReport?.serviceId)!}
                        pet={petToUse}
                        evaluationReport={evaluationReport ?? undefined}
                        onClose={props.onClose}
                        onBack={() => {
                            if (props.defaultViewMode === 'edit') return undefined;
                            setMode('view');
                        }}
                        onSave={props.onSave}
                    />
                )
            )}
        </Drawer>
    );
};
